/**
 * SkillList Component
 * Two-group sortable list with drag-and-drop reordering by display_row
 * Uses single SortableContext + useDroppable containers for cross-row drag
 */

import React from "react";
import { DndContext, DragOverlay, useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SkillRow } from "./SkillRow";
import { useSkillDragDrop } from "../hooks/useSkillDragDrop";
import type { Skill } from "@/types/skills";

interface SkillListProps {
  skills: Skill[];
  onEdit: (skill: Skill) => void;
  onDelete: (skill: Skill) => void;
  onToggle: (skill: Skill) => void;
  onReorder: (
    skillOrders: Array<{
      id: string;
      display_order: number;
      display_row: number;
    }>,
  ) => void;
}

/** Droppable container for a row group */
function DroppableRowGroup({
  id,
  label,
  rowSkills,
  globalStartIndex,
  isDragging,
  onEdit,
  onDelete,
  onToggle,
}: {
  id: string;
  label: string;
  rowSkills: Skill[];
  globalStartIndex: number;
  isDragging: boolean;
  onEdit: (skill: Skill) => void;
  onDelete: (skill: Skill) => void;
  onToggle: (skill: Skill) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`mb-4 transition-colors ${isOver ? "bg-blue-50/50" : ""}`}
    >
      <div
        className={`px-4 py-2 border-b text-sm font-medium transition-colors ${
          isOver
            ? "bg-blue-100 border-blue-300 text-blue-800"
            : "bg-blue-50 border-blue-200 text-blue-700"
        }`}
      >
        {label}（{rowSkills.length}）
      </div>
      <div className="divide-y divide-gray-100">
        {rowSkills.length === 0 ? (
          <div
            className={`px-4 py-6 text-center text-sm transition-colors ${
              isOver ? "text-blue-500 bg-blue-50" : "text-gray-400"
            }`}
          >
            拖拽技能到这里
          </div>
        ) : (
          rowSkills.map((skill, index) => (
            <SkillRow
              key={skill.id}
              skill={skill}
              index={globalStartIndex + index}
              isDragging={isDragging}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggle={onToggle}
            />
          ))
        )}
      </div>
    </div>
  );
}

export const SkillList: React.FC<SkillListProps> = ({
  skills,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
}) => {
  const row0Skills = skills.filter((s) => (s.display_row ?? 0) === 0);
  const row1Skills = skills.filter((s) => (s.display_row ?? 0) === 1);

  const {
    activeId,
    isDragging,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    collisionDetection,
  } = useSkillDragDrop({ onReorder });

  const activeSkill = activeId ? skills.find((s) => s.id === activeId) : null;

  if (skills.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg mb-2">No skills found</p>
        <p className="text-sm">Create your first skill to get started</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={(event) => handleDragEnd(event, row0Skills, row1Skills)}
      onDragCancel={handleDragCancel}
    >
      {/* Single SortableContext with all items for cross-row dragging */}
      <SortableContext
        items={[...row0Skills, ...row1Skills].map((s) => s.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[40px_160px_minmax(180px,1fr)_minmax(200px,1.5fr)_80px_100px] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div></div>
            <div>ID</div>
            <div>Name / Description</div>
            <div>Command</div>
            <div className="text-center">Status</div>
            <div className="text-right">Actions</div>
          </div>

          {/* Row 0 - droppable container */}
          <DroppableRowGroup
            id="row-0-drop"
            label="第一行"
            rowSkills={row0Skills}
            globalStartIndex={0}
            isDragging={isDragging}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggle={onToggle}
          />

          {/* Row 1 - droppable container */}
          <DroppableRowGroup
            id="row-1-drop"
            label="第二行"
            rowSkills={row1Skills}
            globalStartIndex={row0Skills.length}
            isDragging={isDragging}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggle={onToggle}
          />
        </div>
      </SortableContext>

      <DragOverlay>
        {activeSkill ? (
          <div className="opacity-90 bg-white shadow-lg rounded-lg border border-primary-300">
            <SkillRow
              skill={activeSkill}
              index={skills.findIndex((s) => s.id === activeSkill.id)}
              isDragging={false}
              onEdit={() => {}}
              onDelete={() => {}}
              onToggle={() => {}}
              isOverlay
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
